package co.uk.paxes.pinpoint.web

import kotlinx.browser.window

/**
 * RewardedAdManager handles rewarded ad display and undo functionality for the web client.
 * 
 * Uses PropellerAds Direct Link (Zone ID: 10880682)
 * - User clicks "Watch Ad" button
 * - Opens Direct Link URL in new tab
 * - 15-second verification timer
 * - Automatic reward granting
 */
object RewardedAdManager {
    
    private const val STORAGE_KEY = "pinpoint_undo_credits"
    private const val INITIAL_CREDITS = 2
    private const val CREDITS_PER_AD = 1
    private const val MAX_CREDITS = 4
    
    // PropellerAds Direct Link URL
    private const val PROPELLER_DIRECT_LINK = "https://omg10.com/4/10880682"
    
    // State
    private var initialized = false
    
    /**
     * Initialize the rewarded ad SDK.
     */
    fun initialize() {
        if (initialized) return
        
        console.log("RewardedAdManager: Initializing with PropellerAds Direct Link...")
        
        // Export to JS explicitly with an unmangled bridge function
        window.asDynamic().pinpointRewardedAdManager = this
        window.asDynamic().pinpointShowRewardedAd = { onRewardEarned: () -> Unit, onAdClosed: () -> Unit, onAdFailed: () -> Unit, adName: String ->
            showRewardedAd(onRewardEarned, onAdClosed, onAdFailed, adName)
        }
        
        // Export difficulty unlock function directly to window
        window.asDynamic().showRewardedAdForDifficultyUnlock = { difficulty: String, callback: (Boolean) -> Unit ->
            showRewardedAdForDifficultyUnlock(difficulty, callback)
        }
        
        // Also create RewardedAdManager wrapper that delegates to the exported function
        js("""
            window.RewardedAdManager = {
                showRewardedAdForDifficultyUnlock: function(difficulty, callback) {
                    return window.showRewardedAdForDifficultyUnlock(difficulty, callback);
                }
            };
        """)
        
        // Load credits or initialize with starting amount
        val existingCredits = PlayGamaStorageManager.getItem(STORAGE_KEY)
        console.log("RewardedAdManager: Existing credits in storage: $existingCredits")
        
        if (existingCredits == null) {
            console.log("RewardedAdManager: No existing credits, setting initial credits to $INITIAL_CREDITS")
            PlayGamaStorageManager.setItem(STORAGE_KEY, INITIAL_CREDITS.toString())  // ✅ Updated: use PlayGamaStorageManager
        } else {
            // If existing credits are 0, reset to initial amount (for better user experience)
            val currentCredits = existingCredits.toIntOrNull() ?: 0
            if (currentCredits == 0) {
                console.log("RewardedAdManager: Found 0 credits, resetting to $INITIAL_CREDITS for better UX")
                PlayGamaStorageManager.setItem(STORAGE_KEY, INITIAL_CREDITS.toString())  // ✅ Updated: use PlayGamaStorageManager
            }
        }
        
        initialized = true
        console.log("RewardedAdManager: ✅ Initialized with ${getUndoCredits()} credits (PropellerAds Direct Link)")
    }
    
    /**
     * Check if a rewarded ad is ready to show.
     */
    fun isAdReady(): Boolean {
        return true
    }
    
    /**
     * Show a rewarded ad using PropellerAds Direct Link.
     * Opens the Direct Link URL in a new tab and shows verification overlay.
     */
    fun showRewardedAd(
        onRewardEarned: () -> Unit,
        onAdClosed: () -> Unit = {},
        onAdFailed: () -> Unit = {},
        adName: String = "undo_credit"
    ) {
        console.log("RewardedAdManager: 🚀 showRewardedAd called with adName=$adName")
        
        val env = try {
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        } catch (e: Exception) {
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.STANDALONE
        }
        
        // Discord sandbox blocks popups and modals - grant reward directly
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            console.log("RewardedAdManager: 🟣 Discord mode - skipping ad, granting reward directly")
            if (adName == "undo_credit") {
                addUndoCredits(CREDITS_PER_AD)
            }
            onRewardEarned()
            onAdClosed()
            return
        }
        
        // PlayGama: Use bridge rewarded ads
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.PLAYGAMA) {
            console.log("RewardedAdManager: 🎮 PlayGama mode - showing rewarded video ad via bridge")
            
            var rewardGranted = false
            var stateHandler: ((dynamic) -> Unit)? = null
            
            try {
                val bridge: dynamic = js("window.bridge")
                if (bridge != null && bridge.advertisement != null) {
                    val eventName: dynamic = bridge.EVENT_NAME?.REWARDED_STATE_CHANGED ?: "REWARDED_STATE_CHANGED"
                    
                    // Subscribe to state changes
                    stateHandler = { state: dynamic ->
                        val stateStr = state as? String ?: state.toString()
                        console.log("RewardedAdManager: 📢 Rewarded state: $stateStr")
                        
                        // Only grant reward when state == "rewarded"
                        if (stateStr == "rewarded" && !rewardGranted) {
                            rewardGranted = true
                            console.log("RewardedAdManager: ✅ Rewarded state reached - granting reward")
                            if (adName == "undo_credit") {
                                addUndoCredits(CREDITS_PER_AD)
                            }
                            onRewardEarned()
                        }
                        
                        // Handle closed/failed states and cleanup
                        if (stateStr == "closed" || stateStr == "failed") {
                            if (!rewardGranted) {
                                console.log("RewardedAdManager: ❌ Ad closed without reward")
                                onAdFailed()
                            }
                            // CRITICAL: Unsubscribe handler to prevent duplicate rewards
                            if (stateHandler != null) {
                                console.log("RewardedAdManager: 🧹 Unsubscribing from REWARDED_STATE_CHANGED")
                                bridge.advertisement.off(eventName, stateHandler)
                            }
                        }
                    }
                    
                    bridge.advertisement.on(eventName, stateHandler)
                    console.log("RewardedAdManager: Subscribed to REWARDED_STATE_CHANGED")
                    
                    // Now show the ad
                    console.log("RewardedAdManager: Calling bridge.advertisement.showRewarded()")
                    bridge.advertisement.showRewarded("rewarded_video_${adName}")
                    
                    console.log("RewardedAdManager: 🎬 Bridge rewarded ad triggered")
                    onAdClosed()
                    return
                }
            } catch (e: Exception) {
                console.warn("RewardedAdManager: ⚠️ PlayGama bridge error: ${e.message}")
                onAdFailed()
                return
            }
        }
        
        // Open PropellerAds Direct Link in new tab
        try {
            console.log("RewardedAdManager: Opening PropellerAds Direct Link...")
            val adWindow = kotlinx.browser.window.open(PROPELLER_DIRECT_LINK, "_blank")
            
            // Check if popup was blocked (null means blocked)
            if (adWindow == null) {
                console.warn("RewardedAdManager: ⚠️ Popup was blocked by browser")
                window.alert("Please allow popups for this site to watch ads and earn rewards.")
                onAdFailed()
                return
            }
            
            console.log("RewardedAdManager: ✅ Ad window opened successfully")
            
        } catch (e: Exception) {
            console.error("RewardedAdManager: ❌ Failed to open ad: ${e.message}")
            onAdFailed()
            return
        }
        
        val body = kotlinx.browser.document.body ?: return
        
        val overlay = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLDivElement
        overlay.id = "fake-ad-overlay"
        overlay.style.cssText = """
            position: fixed;
            top: 0; left: 0; width: 100%; height: 100%;
            background-color: rgba(0,0,0,0.9);
            z-index: 99999;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: white;
            font-family: sans-serif;
            text-align: center;
        """.trimIndent()
        
        val message = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLDivElement
        message.innerHTML = "<h2>Watching Ad...</h2><p style='color: #ccc; max-width: 80%; margin: 10px auto;'>Thank you for supporting Pinpoint! Your reward will be granted in a moment.</p>"
        
        val timerDisplay = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLDivElement
        timerDisplay.style.cssText = "font-size: 4rem; font-weight: bold; margin: 30px 0; color: #4CAF50;"
        timerDisplay.innerText = "15"
        
        val disclaimer = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLDivElement
        disclaimer.style.cssText = "font-size: 0.85rem; color: #888; max-width: 80%; line-height: 1.5; margin-top: 20px;"
        disclaimer.innerHTML = "Your reward will be granted automatically.<br>The ad may open in a new window."
        
        // Add a cancel button in case user wants to close
        val cancelButton = kotlinx.browser.document.createElement("button") as org.w3c.dom.HTMLButtonElement
        cancelButton.innerText = "Cancel"
        cancelButton.style.cssText = """
            margin-top: 20px;
            padding: 10px 30px;
            background-color: #f44336;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 1rem;
        """.trimIndent()
        
        var intervalId = 0
        
        cancelButton.onclick = {
            console.log("RewardedAdManager: User cancelled ad")
            window.clearInterval(intervalId)
            if (body.contains(overlay)) {
                body.removeChild(overlay)
            }
            onAdClosed()
            null
        }
        
        overlay.appendChild(message)
        overlay.appendChild(timerDisplay)
        overlay.appendChild(disclaimer)
        overlay.appendChild(cancelButton)
        body.appendChild(overlay)
        
        var remaining = 15
        
        intervalId = window.setInterval({
            remaining--
            timerDisplay.innerText = "$remaining"
            
            if (remaining <= 5) {
                timerDisplay.style.color = "#FFC107" // Change color as it gets close
            }
            if (remaining <= 2) {
                timerDisplay.style.color = "#FF5252"
            }
            
            if (remaining <= 0) {
                window.clearInterval(intervalId)
                if (body.contains(overlay)) {
                    body.removeChild(overlay)
                }
                
                if (adName == "undo_credit") {
                    addUndoCredits(CREDITS_PER_AD)
                }
                
                console.log("RewardedAdManager: ✅ Reward earned successfully")
                onRewardEarned()
                onAdClosed()
            }
        }, 1000)
    }
    
    /**
     * Get current number of undo credits.
     */
    fun getUndoCredits(): Int {
        val stored = PlayGamaStorageManager.getItem(STORAGE_KEY)  // ✅ Updated: use PlayGamaStorageManager
        val count = stored?.toIntOrNull() ?: 0
        return count.coerceIn(0, MAX_CREDITS)
    }
    
    /**
     * Add undo credits.
     */
    private fun addUndoCredits(amount: Int = CREDITS_PER_AD) {
        val current = getUndoCredits()
        val newCount = (current + amount).coerceIn(0, MAX_CREDITS)
        PlayGamaStorageManager.setItem(STORAGE_KEY, newCount.toString())  // ✅ Updated: use PlayGamaStorageManager
        console.log("RewardedAdManager: Added $amount credits. New total: $newCount")
    }
    
    /**
     * Add a single undo credit (for refunds when undo fails).
     */
    fun addUndoCredit() {
        addUndoCredits(1)
    }
    
    /**
     * Use one undo credit.
     * @return true if a credit was spent, false if no credits left
     */
    fun useUndoCredit(): Boolean {
        val current = getUndoCredits()
        if (current > 0) {
            val newTotal = current - 1
            PlayGamaStorageManager.setItem(STORAGE_KEY, newTotal.toString())  // ✅ Updated: use PlayGamaStorageManager
            console.log("RewardedAdManager: Spent 1 credit (remaining: $newTotal)")
            return true
        }
        console.log("RewardedAdManager: No credits left to spend")
        return false
    }
    
    /**
     * Check if user has any undo credits.
     */
    fun hasUndoCredits(): Boolean = getUndoCredits() > 0
    
    /**
     * Check if user can earn more credits.
     */
    fun canEarnMoreCredits(): Boolean = getUndoCredits() < MAX_CREDITS
    
    /**
     * Check if player can undo.
     */
    fun canUndo(): Boolean = hasUndoCredits()
    
    /**
     * Consume the undo reward after use.
     */
    fun consumeUndoReward(): Boolean = useUndoCredit()
    
    /**
     * Show rewarded ad for difficulty unlock (Hard/Expert).
     * @param difficulty The difficulty level to unlock ("hard" or "expert")
     * @param callback Success callback (true if ad completed, false if failed/cancelled)
     */
    fun showRewardedAdForDifficultyUnlock(difficulty: String, callback: (Boolean) -> Unit) {
        console.log("RewardedAdManager: 🔓 Showing rewarded ad to unlock $difficulty difficulty")
        
        val env = try {
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        } catch (e: Exception) {
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.STANDALONE
        }
        console.log("RewardedAdManager: Environment detected for difficulty unlock: $env")
        
        showRewardedAd(
            onRewardEarned = {
                console.log("RewardedAdManager: ✅ Difficulty unlock ad completed - $difficulty unlocked")
                callback(true)
            },
            onAdClosed = {
                console.log("RewardedAdManager: 📪 Difficulty unlock ad closed")
            },
            onAdFailed = {
                console.log("RewardedAdManager: ❌ Difficulty unlock ad failed - $difficulty not unlocked")
                callback(false)
            },
            adName = "unlock_$difficulty"
        )
    }
    
    /**
     * Reset credits to initial amount (for debugging/testing).
     */
    fun resetCredits() {
        PlayGamaStorageManager.setItem(STORAGE_KEY, INITIAL_CREDITS.toString())  // ✅ Updated: use PlayGamaStorageManager
        console.log("RewardedAdManager: Credits reset to $INITIAL_CREDITS")
    }
}
